/**
 * Client error capture — evidence only.
 *
 * The Talk white-screen/reload seen on iPhone leaves nothing behind today: there is no error
 * boundary and no client-side error reporting, so an uncaught exception unmounts the tree
 * silently and a Safari tab discard is indistinguishable from a member reload. This records what
 * the browser already knows, to the existing diagnostics endpoint (technical telemetry, 30-day
 * TTL — no member content).
 *
 * It changes no behaviour. It exists so the next occurrence is diagnosable.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;

let lastSignature = null;

// Only the error's class leaves the device ("TypeError", "NotAllowedError"), never its text: a
// browser message can quote what the member typed. The server reduces anything else to a code.
const errorClass = (message) => {
  const m = /\b([A-Z][A-Za-z]{1,40}(?:Error|Exception))\b/.exec((message || '').toString());
  return m ? m[1] : (message ? 'unclassified' : null);
};

const report = async (stage, category, message, metadata = {}) => {
  const signature = `${stage}:${category}:${message}`;
  if (signature === lastSignature) return; // don't loop on a repeating error
  lastSignature = signature;
  try {
    await fetch(`${API_URL}/api/auth/diagnostic`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify({
        event_type: 'client_runtime',
        stage,
        error_category: category,
        error_message: errorClass(message),
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        timestamp: new Date().toISOString(),
        metadata: {
          ...metadata,
          path: window.location.pathname,
          // Safari's memory-pressure discards correlate with these, when the browser exposes them.
          device_memory: navigator.deviceMemory ?? null,
          heap_used: window.performance?.memory?.usedJSHeapSize ?? null,
          heap_limit: window.performance?.memory?.jsHeapSizeLimit ?? null,
          navigation_type: window.performance?.getEntriesByType?.('navigation')?.[0]?.type ?? null,
        },
      }),
    });
  } catch (err) {
    // Never let reporting create a second failure.
  }
};

export const reportClientError = report;

export const installClientErrorReporter = () => {
  window.addEventListener('error', (event) => {
    report('window_error', 'uncaught_exception', event?.message, {
      source: event?.filename, line: event?.lineno, column: event?.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    report('unhandled_rejection', 'unhandled_promise',
      reason?.message || reason, { name: reason?.name || null });
  });

  // A page that comes back as a fresh navigation the member did not ask for is the signature of
  // a browser-side reload (memory pressure, tab discard, renderer restart).
  window.addEventListener('pageshow', (event) => {
    const nav = window.performance?.getEntriesByType?.('navigation')?.[0];
    if (!event.persisted && nav?.type === 'reload') {
      report('page_reloaded', 'reload_observed', 'navigation type=reload', {
        was_persisted: false,
      });
    }
  });
};
